import { PayloadAction } from '@reduxjs/toolkit';
import { call, fork, join, put, select } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/types';
import { push } from 'redux-first-history';
import { HttpStatusCode } from 'axios';
import { toast } from 'react-toastify';

import i18n from '@/i18n';
import { amlbotAPI } from '@/api';

import type {
  BaseRequest,
  BaseSuccessResponse,
  ChangeFullNameEmailRequest,
  ChangeUserPasswordRequest,
  CheckRequest,
  CheckResponse,
  ConfirmEmailRequest,
  ConfirmEmailResponse,
  ForgotPasswordEmailRequest,
  ForgotPasswordEmailResponse,
  GoogleAuthOneTapRequest,
  GoogleAuthOneTapResponse,
  GoogleAuthRequest,
  GoogleAuthResponse,
  GoogleTokenTwoFARequest,
  ReceiveRemindersRequest,
  ReceiveRemindersResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SigninRequest,
  SigninResponse,
  SignoutRequest,
  SignoutResponse,
  SignupRequest,
  SignupRequestExtended,
  SignupResponse,
  TelegramAuthRequest,
  TelegramAuthRequestExtended,
  UpdateCheckModeRequest,
  UpdateCheckModeResponse,
  UpdateLocaleRequest,
  UpdateLocaleResponse,
  UpdateUserResponse,
  VerifyRecoveryTokenRequest,
  VerifyRecoveryTokenResponse,
} from '@/api';

import { COMMON_CONTAINER_ID, ErrorCodeEnum } from '@/config';
import { notifyProVersion, checkIfFromProVersion } from '@/helpers';

import {
  checkMethodModalActions,
  paymentActions,
  personalInfoModalActions,
  receiveRemindersModalActions,
  serviceOptionsActions,
  userActions,
  webSocketActions,
} from '@/redux/actions';

import { parseError } from '@/redux/helpers';
import { userMappers } from '@/redux/mappers';
import { locationSelectors, userSelectors } from '@/redux/selectors';
import { setSentryUser } from '@/config/sentryInit';
import { getProfileInfo } from './profileWorkers';

function changeLocale(locale: string = 'en'): void {
  i18n.changeLanguage(locale);
  localStorage.setItem('lng', locale);
}

function* check(action: PayloadAction<CheckRequest>): SagaIterator {
  try {
    const location = yield select(locationSelectors.getLocation);

    const theRef = new URLSearchParams(location.search).get('ref');

    const requestData: CheckRequest = {
      ref: theRef || undefined,
    };

    const response: CheckResponse = yield call(amlbotAPI.check, requestData);

    const data = userMappers.check(response);
    yield put(userActions.setData(data));

    // Set user info in Sentry
    if (data) {
      setSentryUser(data);
    }

    yield put(
      paymentActions.setDiscounts({
        discount: data?.discount || 0,
        discountDueTime: data?.discountDueTime || 0,
      }),
    );
    yield put(serviceOptionsActions.fetchOptions());
    yield put(webSocketActions.connect());
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    yield put(userActions.setSuspense(false));
  }
}

function* current(): SagaIterator {
  try {
    const response: CheckResponse = yield call(amlbotAPI.current);
    const data = userMappers.current(response);
    changeLocale(data?.locale);
    yield put(userActions.setData(data));
    yield put(
      paymentActions.setDiscounts({
        discount: data?.discount || 0,
        discountDueTime: data?.discountDueTime || 0,
      }),
    );
  } catch (error: unknown) {
    // console.log(error);
  }
}

function* signup(action: PayloadAction<SignupRequestExtended>): SagaIterator {
  try {
    const { hsCallback, gaCallback, ...rest } = action.payload;
    const actualPayload: SignupRequest = rest;

    hsCallback?.(actualPayload.emailAddress, '');
    const response: SignupResponse = yield call(
      amlbotAPI.signup,
      actualPayload,
    );
    gaCallback?.();

    notifyProVersion('signIn');

    const isFromProVersion = checkIfFromProVersion();

    if (!isFromProVersion) {
      yield put(push('/signup-success'));
    } else {
      window.close();
    }
  } catch (error: unknown) {
    const { message } = parseError(error, {
      [HttpStatusCode.Conflict]: i18n.t('translation.email_conflict_error'),
    });
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* signin(action: PayloadAction<SigninRequest>): SagaIterator {
  try {
    const { payload } = action;
    const response: SigninResponse = yield call(amlbotAPI.signin, payload);
    const data = userMappers.signin(response);

    changeLocale(data?.locale);
    yield put(userActions.setData(data));
    yield put(
      paymentActions.setDiscounts({
        discount: data?.discount || 0,
        discountDueTime: data?.discountDueTime || 0,
      }),
    );
    yield put(serviceOptionsActions.fetchOptions());

    notifyProVersion('signIn');

    const isFromProVersion = checkIfFromProVersion();

    if (!isFromProVersion) {
      yield put(webSocketActions.connect());
      yield put(push('/'));
    } else {
      window.close();
    }
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);

    if (errorCode) {
      yield put(userActions.setErrorCode(errorCode));

      switch (errorCode) {
        case ErrorCodeEnum.USER_LOGGED_IN:
          yield put(
            userActions.setError(i18n.t('translation.already_logged_in')),
          );
          break;
        case ErrorCodeEnum.TWO_FA_ERROR:
        case ErrorCodeEnum.TWO_FA_REQUIRED:
          yield put(
            userActions.setError(i18n.t('translation.enter_2fa_code_str')),
          );
          break;
        case ErrorCodeEnum.VALIDATION:
          yield put(
            userActions.setError(i18n.t('translation.password_signin_error')),
          );
          break;
        default:
          break;
      }
    } else {
      yield put(userActions.setError(message));
    }
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* googleAuth(
  action: PayloadAction<GoogleTokenTwoFARequest>,
): SagaIterator {
  try {
    const {
      code,
      pendingToken,
      token2FA,
      hsCallback,
      gaCallback,
      amlCheckFor,
    } = action.payload;

    const payload: GoogleAuthRequest = {
      ...(code ? { code } : { pendingToken }),
      token2fa: token2FA,
    };

    if (!!amlCheckFor) {
      payload.amlCheckFor = amlCheckFor;
    }

    const response: GoogleAuthResponse = yield call(
      amlbotAPI.googleAuth,
      payload,
    );
    gaCallback?.();

    yield put(userActions.setPendingGoogleToken(null));

    const data = userMappers.signinGoogle(response);

    hsCallback?.(data?.emailAddress, '');
    changeLocale(data?.locale);

    yield put(userActions.setData(data));
    yield put(
      paymentActions.setDiscounts({
        discount: data?.discount || 0,
        discountDueTime: data?.discountDueTime || 0,
      }),
    );
    yield put(serviceOptionsActions.fetchOptions());

    notifyProVersion('signIn');

    const isFromProVersion = checkIfFromProVersion();

    if (!isFromProVersion) {
      yield put(webSocketActions.connect());
      yield put(push('/'));
    } else {
      window.close();
    }
  } catch (error: unknown) {
    const { message, errorCode, pendingToken } = parseError(error, {
      [HttpStatusCode.Conflict]: i18n.t('translation.email_conflict_error'),
    });

    if (errorCode) {
      if (pendingToken) {
        yield put(userActions.setPendingGoogleToken(pendingToken));
      }
      yield put(userActions.setErrorCode(errorCode));
      yield put(userActions.setError(i18n.t('translation.enter_2fa_code_str')));
    } else {
      yield put(userActions.setError(message));
    }
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* googleAuthOneTap(
  action: PayloadAction<GoogleAuthOneTapRequest>,
): SagaIterator {
  try {
    const { token, amlCheckFor, hsCallback, gaCallback } = action.payload;
    const payload: GoogleAuthOneTapRequest = { token, amlCheckFor };
    const response: GoogleAuthOneTapResponse = yield call(
      amlbotAPI.googleAuthOneTap,
      payload,
    );

    const data = userMappers.signinGoogle(response);

    if (data) {
      hsCallback?.(data.emailAddress);
      gaCallback?.();
    }

    changeLocale(data?.locale);

    yield put(userActions.setData(data));
    yield put(
      paymentActions.setDiscounts({
        discount: data?.discount || 0,
        discountDueTime: data?.discountDueTime || 0,
      }),
    );
    yield put(serviceOptionsActions.fetchOptions());

    notifyProVersion('signIn');

    const isFromProVersion = checkIfFromProVersion();

    if (!isFromProVersion) {
      yield put(webSocketActions.connect());
      yield put(push('/'));
    } else {
      window.close();
    }
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error, {
      [HttpStatusCode.Conflict]: i18n.t('translation.email_conflict_error'),
    });
    if (errorCode) {
      yield put(userActions.setErrorCode(errorCode));
      yield put(userActions.setError(message));
    } else {
      yield put(userActions.setError(message));
    }
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* telegramAuth(
  action: PayloadAction<TelegramAuthRequestExtended>,
): SagaIterator {
  try {
    const { payload } = action;
    const { gaCallback, ...rest } = payload;
    const actualPayload: TelegramAuthRequest = rest;

    const response = yield call(amlbotAPI.telegramAuth, actualPayload);
    gaCallback?.();

    const data = userMappers.signin(response);
    yield put(userActions.setData(data));
    yield put(
      paymentActions.setDiscounts({
        discount: data?.discount || 0,
        discountDueTime: data?.discountDueTime || 0,
      }),
    );
    yield put(serviceOptionsActions.fetchOptions());

    notifyProVersion('signIn');

    const isFromProVersion = checkIfFromProVersion();

    if (!isFromProVersion) {
      yield put(webSocketActions.connect());
      yield put(push('/'));
    } else {
      window.close();
    }
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* signout(action: PayloadAction<SignoutRequest>): SagaIterator {
  try {
    const response: SignoutResponse = yield call(amlbotAPI.signout);
    notifyProVersion('signOut');

    // Clear user info in Sentry
    setSentryUser(null);
  } catch (error: unknown) {
    // console.log(error);
  } finally {
    if (window.location.pathname !== '/signin') {
      window.location.href = '/signin';
    }
  }
}

function* confirmEmail(
  action: PayloadAction<ConfirmEmailRequest>,
): SagaIterator {
  try {
    const { payload } = action;

    if (!payload.token) {
      const missingTokenMessage = i18n.t('translation.token_not_found');
      yield put(userActions.setError(missingTokenMessage));
    } else {
      const response: ConfirmEmailResponse = yield call(
        amlbotAPI.confirmEmail,
        payload,
      );

      yield fork(current);
      yield put(push('/email/confirm-success'));
    }
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error, {
      [HttpStatusCode.Conflict]: i18n.t(
        'translation.confirm_email_conflict_error',
      ),
      [HttpStatusCode.NotFound]: i18n.t('translation.token_user_not_found'),
    });

    if (errorCode === ErrorCodeEnum.TOKEN_EXPIRED) {
      yield put(
        userActions.setError(i18n.t('translation.confirm_token_expire_error')),
      );
    } else {
      yield put(userActions.setError(message));
    }
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* forgotPasswordEmail(
  action: PayloadAction<ForgotPasswordEmailRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: ForgotPasswordEmailResponse = yield call(
      amlbotAPI.passwordRecoveryEmail,
      payload,
    );
    yield put(push('/password/forgot-success'));
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);
    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* verifyRecoveryToken(
  action: PayloadAction<VerifyRecoveryTokenRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: VerifyRecoveryTokenResponse = yield call(
      amlbotAPI.verifyRecoveryToken,
      payload,
    );
    // TODO: handle success
  } catch (error: unknown) {
    // TODO: handle error

    console.log(error);
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* resetPassword(
  action: PayloadAction<ResetPasswordRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: ResetPasswordResponse = yield call(
      amlbotAPI.resetPassword,
      payload,
    );

    yield put(push('/password/new-success'));
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error, {
      [HttpStatusCode.NotFound]: i18n.t('translation.token_user_not_found'),
    });

    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    if (errorCode === ErrorCodeEnum.TOKEN_EXPIRED) {
      yield put(
        userActions.setError(i18n.t('translation.confirm_token_expire_error')),
      );
    } else {
      yield put(userActions.setError(message));
    }
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* changeFullNameEmail(
  action: PayloadAction<ChangeFullNameEmailRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: UpdateUserResponse = yield call(
      amlbotAPI.changeFullNameEmail,
      payload,
    );
    const data = userMappers.changeFullNameEmail(response);
    toast.success(i18n.t('translation.toast_profile_updated'), {
      containerId: COMMON_CONTAINER_ID,
    });
    yield put(personalInfoModalActions.closeModal());
    yield put(userActions.setData(data));
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error, {
      [HttpStatusCode.Conflict]: i18n.t('translation.email_conflict_error'),
      [HttpStatusCode.MethodNotAllowed]: i18n.t(
        'translation.email_change_not_allowed',
      ),
    });
    // Optionally handle errorCode - ErrorCodeEnum.VALIDATION
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* changeUserPassword(
  action: PayloadAction<ChangeUserPasswordRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: BaseSuccessResponse = yield call(
      amlbotAPI.changeUserPassword,
      payload,
    );
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);
    // Optionally handle errorCode - ErrorCodeEnum.FIELD_REQUIRED
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* updateCheckMode(
  action: PayloadAction<UpdateCheckModeRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: UpdateCheckModeResponse = yield call(
      amlbotAPI.updateCheckMode,
      payload,
    );
    const { extBalance, amlFlow } = response.data;
    yield put(userActions.updateAttribute({ extBalance, amlFlow }));

    toast.success(i18n.t('translation.toast_amlflow_updated'), {
      containerId: COMMON_CONTAINER_ID,
    });

    yield put(checkMethodModalActions.closeModal());
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* updateLocale(
  action: PayloadAction<UpdateLocaleRequest>,
): SagaIterator {
  try {
    const isAuth = yield select(userSelectors.getAuth);
    if (isAuth) {
      const { payload } = action;
      const response: UpdateLocaleResponse = yield call(
        amlbotAPI.updateLocale,
        payload,
      );

      if (response.success) {
        const taskId = yield fork(getProfileInfo);
        yield join(taskId);
        yield put(userActions.updateAttribute({ locale: payload.locale }));
      }
    }
  } catch (error: unknown) {
    const { message, errorCode } = parseError(error);
    // Optionally handle errorCode - ErrorCodeEnum.VALIDATION || ErrorCodeEnum.FIELD_REQUIRED
  }
}

function* confirmTerms(action: PayloadAction<BaseRequest>): SagaIterator {
  try {
    const { payload } = action;
    yield call(amlbotAPI.confirmTerms, payload);

    window.location.reload();
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* changeReceiveReminders(
  action: PayloadAction<ReceiveRemindersRequest>,
): SagaIterator {
  try {
    const { payload } = action;
    const response: ReceiveRemindersResponse = yield call(
      amlbotAPI.changeReceiveReminders,
      payload,
    );

    const { receiveReminders } = response.data;
    yield put(userActions.updateAttribute({ receiveReminders }));

    toast.success(i18n.t('translation.receive_reminders_updated'), {
      containerId: COMMON_CONTAINER_ID,
    });

    yield put(receiveRemindersModalActions.closeModal());
  } catch (error: unknown) {
    const { message } = parseError(error);
    yield put(userActions.setError(message));
  } finally {
    yield put(userActions.setLoading(false));
  }
}

function* discardAmlDetailsFor(): SagaIterator {
  try {
    yield call(amlbotAPI.discardAmlDetailsFor, {});
  } catch (error: unknown) {
    //
  } finally {
    yield put(userActions.updateAttribute({ amlDetailsFor: null }));
  }
}

export {
  check,
  current,
  signup,
  signin,
  googleAuth,
  telegramAuth,
  signout,
  confirmEmail,
  forgotPasswordEmail,
  verifyRecoveryToken,
  resetPassword,
  changeFullNameEmail,
  changeUserPassword,
  updateCheckMode,
  updateLocale,
  confirmTerms,
  changeReceiveReminders,
  googleAuthOneTap,
  discardAmlDetailsFor,
};
